import { useState, useEffect } from "react"
import { Search } from "lucide-react"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { useToast } from "@/components/ui/use-toast"
import { supabase } from "@/integrations/supabase/client"

interface OpportunitySearchProps {
  onFiltersChange: (filters: {
    focus_areas?: string[]
    beneficiary_types?: string[]
    opportunity_type?: string
    subtype?: string
    sdg_alignment?: string[]
    format?: string
    duration?: string
    employee_resource_group?: string
    title?: string
    description?: string
    location?: string
    min_volunteers?: number
    max_volunteers?: number
    min_price?: number
    max_price?: number
    volunteer_output?: string
  }) => void
}

const PLACEHOLDER_EXAMPLES = [
  "'virtual workshops lasting 1 hour'",
  "'in-person mentoring for Asian Community'",
  "'virtual training sessions for Women's Network'",
  "'hybrid events for Black Community'"
]

export const OpportunitySearch = ({ onFiltersChange }: OpportunitySearchProps) => {
  const [searchText, setSearchText] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [currentExample, setCurrentExample] = useState("")
  const [placeholderIndex, setPlaceholderIndex] = useState(0)
  const { toast } = useToast()

  useEffect(() => {
    let currentText = ""
    let currentIndex = 0
    let isTyping = true
    let timeout: NodeJS.Timeout

    const animateExample = () => {
      const targetText = PLACEHOLDER_EXAMPLES[placeholderIndex]

      if (isTyping) {
        if (currentIndex < targetText.length) {
          currentText = targetText.slice(0, currentIndex + 1)
          currentIndex++
        } else {
          isTyping = false
          timeout = setTimeout(() => {
            isTyping = false
          }, 1500) // Pause at the end
        }
      } else {
        if (currentIndex > 0) {
          currentText = targetText.slice(0, currentIndex - 1)
          currentIndex--
        } else {
          isTyping = true
          setPlaceholderIndex((current) => (current + 1) % PLACEHOLDER_EXAMPLES.length)
        }
      }

      setCurrentExample(currentText)
      timeout = setTimeout(animateExample, isTyping ? 50 : 30) // Type slower, delete faster
    }

    animateExample()

    return () => clearTimeout(timeout)
  }, [placeholderIndex])

  const handleSearch = async () => {
    if (!searchText.trim()) return

    setIsLoading(true)
    try {
      const { data, error } = await supabase.functions.invoke('parse-search', {
        body: { searchText }
      })

      if (error) throw error
      // Extract filters from the response
      const searchFilters = data.filters || {};

      // Pass through the data directly without transformation
      const filters = {
        focus_areas: searchFilters.focus_areas || [],
        beneficiary_types: searchFilters.beneficiary_types || [],
        opportunity_type: searchFilters.opportunity_type || undefined,
        subtype: searchFilters.subtype || undefined,
        sdg_alignment: searchFilters.sdg_alignment || [],
        format: searchFilters.format || undefined,
        duration: searchFilters.duration || undefined,
        employee_resource_group: searchFilters.employee_resource_group || undefined,
        title: searchFilters.title,
        description: searchFilters.description,
        location: searchFilters.location,
        min_volunteers: searchFilters.min_volunteers,
        max_volunteers: searchFilters.max_volunteers,
        min_price: searchFilters.min_price,
        max_price: searchFilters.max_price,
        volunteer_output: searchFilters.volunteer_output
      }

      console.log('Applying filters:', filters);
      onFiltersChange(filters)
      
      toast({
        title: "Search applied",
        description: "The filters have been updated based on your search.",
      })
    } catch (error) {
      console.error('Search error:', error)
      toast({
        title: "Search failed",
        description: "Failed to process your search. Please try again.",
        variant: "destructive"
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="relative">
      <div className="relative flex items-center">
        <Search className="absolute left-3 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder={`Search opportunities (e.g., ${currentExample})`}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          className="pl-9 pr-24 h-10 rounded-full border-2 focus-visible:ring-primary"
        />
        <Button 
          onClick={handleSearch} 
          disabled={isLoading}
          className="absolute right-0 rounded-full px-4 h-10"
          variant="ghost"
        >
          {isLoading ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary" />
          ) : (
            "Search"
          )}
        </Button>
      </div>
    </div>
  )
}